import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DisplayMode, SelectBox } from 'cbc-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CodeConversionMstDto, Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo } from 'sg-api';
import { CodeConversionMstService } from '../../../../../../sg-base/sg-base-shared/services/codeConversionMst.service';

@Component({
  selector: 'app-sgke380d-search-condition',
  templateUrl: './sgke380d-search-condition.component.html',
  styleUrl: './sgke380d-search-condition.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke380dSearchConditionComponent implements OnInit, OnDestroy {
  /** 画面モード（サービサー委託対象検索画面/サービサー返却検索画面） */
  @Input() task: string;
  /** 検索済フラグ */
  @Input() searched: boolean;
  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報（将来用） */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;

  /** クリアボタン押下イベント */
  @Output() onClickClearEvent = new EventEmitter<UntypedFormGroup>();

  /** 検索ボタン押下イベント */
  @Output() onClickSearchEvent = new EventEmitter<UntypedFormGroup>();

  /** コード変換マスターマップ */
  codeMap: Map<string, CodeConversionMstDto[]>;

  /**
   * 督促管理区分コード 画面項目IDコード値
   */
  static S300090: string = "S300090";
  /**
   * 督促管理区分コード コード値
   */
  /** 無訪問 */
  static S300090_NOVISIT: String = "1";
  /** センター管理 */
  static S300090_CENTERMANAGE: String = "2";
  /** 営業統括管理 */
  static S300090_SALESMANAGE: String = "3";

  /**
   * 会社コード 画面項目IDコード値
   */
  static S900020: string = "S900020";
  /**
   * 地区コード 画面項目IDコード値
   */
  static S900030: string = "S900030";
  /**
   * 請求料金区分コード 画面項目IDコード値
   */
  static S300620: string = "S300620";
  /**
   * サービサー委託ステータスコード（サービサー委託対象検索） 画面項目IDコード値
   */
  static S300001: string = "S300001";
  /**
   * サービサー委託ステータスコード（サービサー返却検索） 画面項目IDコード値
   */
  static S300002: string = "S300002";

  /**
   * サービサー委託ステータス コード値
   */
  /** 委託対象 */
  static S300000_ASSIGN: String = "02";
  /** 委託対象外 */
  static S300000_NOASSIGN: String = "01";
  /** 委託保留 */
  static S300000_ASSIGNHOLD: String = "00";
  /** 返却（可否登録待ち） */
  static S300000_RETURNPENDING: String = "06";
  /** 再委託可 */
  static S300000_REASSIGNABLE: String = "08";
  /** 再委託対象外 */
  static S300000_NOREASSIGN: String = "09";

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();
  /** 親フォーム情報 */
  private parentForm: UntypedFormGroup;
  /** 初期化フラグ */
  private iniFlag: boolean = false;

  /** モード区分_登録 */
  public MODE_CREATE = DisplayMode.create;
  /** モード区分_更新 */
  public MODE_UPDATE = DisplayMode.update;

  /** 検索条件FormGroup */
  searchConditionForm: UntypedFormGroup;
  /** 会社リスト */
  companyList: SelectBox[] = [];
  /** 地区リスト */
  chikuList: SelectBox[] = [];

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {CodeConversionMstService} codeConvert
   * @param {UntypedFormBuilder} fb
   * @memberof Sgke380dSearchConditionComponent
   */
  constructor(
    private parent: FormGroupDirective,
    private codeConvert: CodeConversionMstService,
    private fb: UntypedFormBuilder,
  ) {
  }

  /**
   * サービスの開放
   * @memberof Sgke380dSearchConditionComponent
   */
  ngOnDestroy(): void {
    //購読を停止する処理
    this.onDestroy$.next();
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('searchCndt');
  }

  /**
   * サービサー委託ステータスのコード値取得
   * @readonly
   * @type {Map<string, CodeConversionMstDto[]>}
   * @memberof Sgke380dSearchConditionComponent
   */
  get statusList() {
    if (this.task == this.MODE_UPDATE) {
      return this.codeMap?.get(Sgke380dSearchConditionComponent.S300002);
    }
    return this.codeMap?.get(Sgke380dSearchConditionComponent.S300001);
  }

  /**
   * 画面モード別のサービサー委託ステータスの選択
   * @readonly
   * @type {*}
   * @memberof Sgke380dSearchConditionComponent
   */
  get status() {
    if (this.task == this.MODE_UPDATE) {
      // サービサー委託返却検索画面の場合
      return [Sgke380dSearchConditionComponent.S300000_RETURNPENDING,
      Sgke380dSearchConditionComponent.S300000_REASSIGNABLE,
      Sgke380dSearchConditionComponent.S300000_NOREASSIGN];

    } else {
      // サービサー委託対象検索画面の場合
      return [Sgke380dSearchConditionComponent.S300000_ASSIGN,
      Sgke380dSearchConditionComponent.S300000_NOASSIGN,
      Sgke380dSearchConditionComponent.S300000_ASSIGNHOLD];
    }
  }

  /**
   * 初期処理
   * @memberof Sgke380dSearchConditionComponent
   */
  ngOnInit() {
    this.parentForm = this.parent.form;

    this.iniFlag = true;
    // セレクターに値をセットする
    this.companyList = this.getSelector('companycode', 'companycodeAname', this.cmpnyAreaList);
    this.chikuList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);

    // formを初期値指定で初期化
    this.searchConditionForm = this.fb.group({
      companycode: this.fb.nonNullable.control<string>({ value: null, disabled: false }),
      pressTantoAreacode: this.fb.nonNullable.control<string>({ value: null, disabled: false }),
      pressManagementdivisionCode: this.fb.nonNullable.control({
        value: [Sgke380dSearchConditionComponent.S300090_NOVISIT,
        Sgke380dSearchConditionComponent.S300090_CENTERMANAGE], disabled: false
      }, Validators.required),
      chargeDivcd: this.fb.nonNullable.control({ value: null, disabled: false }),
      servicerCommissionstatusCode: this.fb.nonNullable.control({ value: this.status, disabled: false }, Validators.required),
    });

    // 親コンポーネントのformに検索条件formを追加
    this.parentForm.addControl("cndt", this.searchConditionForm);

    // コード変換マスタ取得
    let dispItemIdList = [
      Sgke380dSearchConditionComponent.S300090,
      Sgke380dSearchConditionComponent.S900020,
      Sgke380dSearchConditionComponent.S900030,
      Sgke380dSearchConditionComponent.S300620,
      Sgke380dSearchConditionComponent.S300001,
      Sgke380dSearchConditionComponent.S300002,
    ];
    // コード変換マスタ共通コンポーネント呼び出し
    this.codeConvert.getCodeConversionMst(dispItemIdList)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(codeMap => {
        // ローカルに保存
        this.codeMap = codeMap;
      });

  }

  /**
   * コントロール情報取得
   * @param {*} ctrlName
   * @returns {UntypedFormControl}
   * @memberof Sgke380dSearchConditionComponent
   */
  getControl(ctrlName): UntypedFormControl {
    return this.searchConditionForm?.get(ctrlName) as UntypedFormControl;
  }

  /**
   * 地区コードリストのクリア制御
   * @param {*} companycode 
   * @memberof Sgke380dSearchConditionComponent
   */
  onChangeCompany(companycode) {
    // 会社コードリストの値が変わった場合
    const chikuCtl = this.getControl('pressTantoAreacode') as UntypedFormControl;
    chikuCtl?.setValue(null);
    if (companycode) {
      // 会社コードが選択されている場合
      const newAreaList: Array<Sgke903aResponseCompanyAreaInfo> = this.cmpnyAreaList
        .filter(list => list.companycode == companycode);
      this.chikuList = this.getSelector('areacode', 'areacodeAname', newAreaList);
    } else {
      // 会社コード
      this.chikuList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);
    }
  }

  /**
   * クリアボタン押下時の処理
   * @memberof Sgke380dSearchConditionComponent
   */
  onClickClear(): void {
    this.searchConditionForm?.reset();
    this.onClickClearEvent.emit();
    this.onChangeCompany(null);
  }
  /**
   * SGKE903Aレスポンスからセレクターにオプションをセット
   * @private
   * @param {string} value
   * @param {string} viewValue
   * @return {SelectBox[]}
   * @memberof Sgke400dConditionComponent
   */
  private getSelector(value: string, viewValue: string, companyAreaInfoList: Array<Sgke903aResponseCompanyAreaInfo>): SelectBox[] {

    // SGKE903Aレスポンスのコードとコード値をまとめてリスト化
    let retSelectBoxList: SelectBox[] = [];
    companyAreaInfoList.map(data => {
      const select: SelectBox = { value: data[value], viewValue: data[viewValue] };
      retSelectBoxList.push(select);
    });

    // 重複を削除しソート
    const uniqueSortedArray = Array.from(
      new Map(retSelectBoxList.map(item => [item.value, item])).values()
    ).sort((a, b) => a.value > b.value ? 1 : -1);

    return uniqueSortedArray;
  }

}
